import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Clock, Plus, Star } from "lucide-react";
import BookingPartnerLogo from "@/components/yts/BookingPartnerLogo";
import AdSlideshow from "@/components/yts/ads/AdSlideshow";

/**
 * The one shape every banner on the site takes: a small photo plate on the
 * left running the experience's own photographs, the offer set beside it in a
 * serif headline, and the facts a visitor decides on — price, rating, length —
 * along the bottom, with the action at the card's end. A sold space, an
 * affiliate experience and a house fill all read as the same card and measure
 * the same height.
 *
 * The card is a warm off-white sheet finished with a crisp near-black outline:
 * an editorial note on the page rather than a billboard fighting the rows
 * around it. The whole card is one link; the slideshow's own controls sit above
 * that link so they never send anyone off-site.
 */
const SHELL =
  "group relative flex w-full items-center gap-2.5 overflow-hidden rounded-[4px] border border-[#DDDBD5] bg-white p-2.5 transition-[box-shadow,background-color] duration-300 hover:bg-white hover:shadow-[0_8px_26px_-20px_rgba(20,18,15,.45)] dark:border-[#f2efe9]/70 dark:bg-[#1c1a17] dark:hover:bg-[#232019] sm:gap-3.5 sm:p-3";

const BRAND_LABEL = { viator: "viator", fareharbor: "FareHarbor", getyourguide: "GetYourGuide" };

const OVERLAY =
  "absolute inset-0 z-10 rounded-[4px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#171717]/60 focus-visible:ring-inset dark:focus-visible:ring-[#f2efe9]/60";

/**
 * The partner's own mark and the line that says what kind of thing it is —
 * the logo, not a line of small print.
 */
function BrandLine({ brand, overline }) {
  const name = brand ? BRAND_LABEL[brand] || brand : "";

  return (
    <span className="flex min-w-0 items-center gap-2">
      {name ? (
        <span className="flex shrink-0 items-center">
          {brand === "viator" || brand === "fareharbor" ? (
            <span className="inline-flex min-h-[25px] items-center">
              <BookingPartnerLogo provider={brand} className="!h-[15px] sm:!h-[17px]" />
            </span>
          ) : (
            <span className="text-[11px] font-black leading-none tracking-[-0.03em] text-[#171717] dark:text-white">
              {name}
            </span>
          )}
        </span>
      ) : null}
      {overline ? (
        <>
          {name ? <span aria-hidden="true" className="h-2.5 w-px shrink-0 bg-[#171717]/25 dark:bg-white/25" /> : null}
          <span className="min-w-0 truncate text-[8.5px] font-bold uppercase tracking-[0.15em] text-[#8a8680] dark:text-white/60">
            {overline}
          </span>
        </>
      ) : null}
    </span>
  );
}

/**
 * One fact from the banner's meta line. A price reads as a tinted pill, a
 * rating keeps its star, a length keeps its clock, and anything else is set as
 * plain ink — so a partner's line never wears an icon that lies about it.
 */
function StatCell({ text }) {
  const price = text.match(/^(from|desde)\s+(\S.*)$/i);
  if (price) {
    return (
      <span className="inline-flex items-baseline gap-1 rounded-full bg-[#efece1] px-2 py-[3px] text-[10px] leading-none text-[#14130f] dark:bg-white/[0.1] dark:text-white">
        <span className="font-semibold">{price[1]}</span>
        <span className="font-black tracking-[-0.01em]">{price[2]}</span>
      </span>
    );
  }

  if (text.startsWith("★")) {
    const value = text.replace("★", "").trim();
    const parts = value.match(/^([\d.]+)\s*(.*)$/);
    return (
      <span className="inline-flex items-center gap-1 text-[10.5px]">
        <Star aria-hidden="true" className="h-2.5 w-2.5 shrink-0 fill-[#f0b429] text-[#f0b429]" />
        <span className="font-black text-[#14130f] dark:text-white">{parts ? parts[1] : value}</span>
        {parts?.[2] ? <span className="font-medium text-[#77736c] dark:text-white/60">{parts[2]}</span> : null}
      </span>
    );
  }

  if (/^[\d½.,\s]*(h|hr|hrs|hour|hours|min|mins|minute|minutes|día|días|dia|dias|day|days|night|nights)\b/i.test(text)) {
    return (
      <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-[#3f3f3a] dark:text-white/75">
        <Clock aria-hidden="true" className="h-2.5 w-2.5 shrink-0 text-[#8a8680] dark:text-white/50" />
        {text}
      </span>
    );
  }

  return <span className="text-[10.5px] font-semibold text-[#3f3f3a] dark:text-white/75">{text}</span>;
}

export default function AdStrip({
  to,
  href,
  brand,
  overline,
  title,
  description,
  meta,
  thumb,
  thumbs,
  thumbAlt = "",
  ariaLabel,
  cta,
  icon: Icon,
  compact = false,
}) {
  const stats = String(meta || "")
    .split(" · ")
    .map((part) => part.trim())
    .filter(Boolean);

  const photos = (thumbs && thumbs.length ? thumbs : [thumb]).filter(Boolean);

  return (
    <div className={SHELL}>
      <AdSlideshow photos={photos} icon={Icon} alt={thumbAlt || title || ""} compact={compact} />

      <span className="relative z-0 flex min-w-0 flex-1 flex-col justify-center gap-1.5 py-0.5">
        <span className="min-w-0">
          <BrandLine brand={brand} overline={overline} />
          <span
            className={`mt-1 line-clamp-2 block font-sans font-extrabold leading-[1.15] tracking-[-0.025em] text-[#111111] dark:text-white ${
              compact ? "text-[14px]" : "text-[15px] sm:text-[17px]"
            }`}
          >
            {title}
          </span>
          {description ? (
            <span className="mt-1 line-clamp-1 hidden text-[11.5px] font-normal leading-[1.4] text-[#55534e] dark:text-[#b7b1a7] sm:block">
              {description}
            </span>
          ) : null}
        </span>

        <span className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
          {stats.length ? (
            <span className="flex min-w-0 flex-wrap items-center">
              {stats.map((stat, index) => (
                <span
                  key={`${stat}-${index}`}
                  className={
                    index === 0
                      ? "flex items-center"
                      : "ml-2 flex items-center border-l border-[#171717]/12 pl-2 dark:border-white/15"
                  }
                >
                  <StatCell text={stat} />
                </span>
              ))}
            </span>
          ) : (
            <span />
          )}

          {cta ? (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-[3px] bg-[#171717] py-[6px] pl-3 pr-2 text-[9px] font-black uppercase tracking-[0.1em] text-white transition-colors duration-300 group-hover:bg-[#c62e2a] dark:bg-[#f2efe9] dark:text-[#131211] dark:group-hover:bg-white">
              {cta}
              <ArrowRight aria-hidden="true" className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-0.5" />
            </span>
          ) : (
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-[3px] bg-[#171717] text-white transition-colors group-hover:bg-[#c62e2a] dark:bg-[#f2efe9] dark:text-[#131211] dark:group-hover:bg-white">
              {Icon ? <Plus aria-hidden="true" className="h-3.5 w-3.5" /> : <ArrowUpRight aria-hidden="true" className="h-3.5 w-3.5" />}
            </span>
          )}
        </span>
      </span>

      {/* One stretched link over the card: the whole banner is the offer, while
          the slideshow's arrows and dots sit above it and keep their own clicks. */}
      {to ? (
        <Link to={to} aria-label={ariaLabel} className={OVERLAY} />
      ) : (
        <a href={href} target={brand === "fareharbor" ? undefined : "_blank"} rel="noopener noreferrer sponsored" aria-label={ariaLabel} className={OVERLAY} />
      )}
    </div>
  );
}